import React, { useState, useEffect } from "react";
import {
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Slide,
  Typography,
} from "@mui/material";
import TableBarIcon from "@mui/icons-material/TableBar";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import LocalBarIcon from "@mui/icons-material/LocalBar";
import RadioIcon from "@mui/icons-material/Radio";
import "./TableModal.css";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
const API = process.env.REACT_APP_API_BASE;

const TableModal = ({ open, onClose, initialData, onSuccess, weddingId }) => {
  const isEdit = Boolean(initialData);
  const [mode, setMode] = useState("single");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    number: "",
    seat_count: "",
    type: "round",
  });
  const [bulkFormData, setBulkFormData] = useState({
    count: "",
    seat_count: "",
    type: "square",
  });

  const [errors, setErrors] = useState({
    number: false,
    seat_count: false,
  });
  const [bulkErrors, setBulkErrors] = useState({
    count: false,
    seat_count: false,
  });

  useEffect(() => {
    if (open) {
      setMode("single");
      setSubmitting(false);
      setSubmitError("");
      if (!initialData) {
        setFormData({
          name: "",
          number: "",
          seat_count: "",
          type: "round",
        });
        setBulkFormData({
          count: "",
          seat_count: "",
          type: "square",
        });
      } else {
        const calculatedSeatCount =
          initialData.availableSeats + initialData.takenSeats;
        setFormData({
          ...initialData,
          number: initialData.number?.toString() || "",
          seat_count: calculatedSeatCount?.toString() || "",
        });
      }
      setErrors({ number: false, seat_count: false });
      setBulkErrors({ count: false, seat_count: false });
    }
  }, [open, initialData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberInputChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      setErrors((prev) => ({ ...prev, [name]: true }));
    }
  };

  const handleBulkNumberInputChange = (e) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) {
      setBulkFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setBulkErrors((prev) => ({ ...prev, [name]: false }));
    } else {
      setBulkErrors((prev) => ({ ...prev, [name]: true }));
    }
  };

  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setFormData((prev) => ({
        ...prev,
        type: newType,
      }));
    }
  };

  const handleBulkTypeChange = (event, newType) => {
    if (newType !== null) {
      setBulkFormData((prev) => ({
        ...prev,
        type: newType,
      }));
    }
  };

  const handleSingleSubmit = async () => {
    const numberValid = /^\d+$/.test(formData.number);
    const seatValid =
      /^\d+$/.test(formData.seat_count) &&
      parseInt(formData.seat_count, 10) > 0;

    if (!numberValid || !seatValid) {
      setErrors({
        number: !numberValid,
        seat_count: !seatValid,
      });
      return;
    }

    const url = initialData
      ? `${API}/tables/${initialData.id}`
      : `${API}/tables`;
    const method = initialData ? "PUT" : "POST";

    const body = {
      name: formData.name,
      number: parseInt(formData.number, 10),
      seat_count: parseInt(formData.seat_count, 10),
      type: formData.type,
      wedding_id: weddingId,
    };

    try {
      setSubmitting(true);
      setSubmitError("");
      const response = await fetchWithAuth(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Error saving table data");
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving table:", error);
      setSubmitError(error.message || "Настана грешка при зачувување на масата.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkSubmit = async () => {
    const countValid =
      /^\d+$/.test(bulkFormData.count) && parseInt(bulkFormData.count, 10) > 0;
    const seatValid =
      /^\d+$/.test(bulkFormData.seat_count) &&
      parseInt(bulkFormData.seat_count, 10) > 0;

    if (!countValid || !seatValid) {
      setBulkErrors({
        count: !countValid,
        seat_count: !seatValid,
      });
      return;
    }

    const body = {
      count: parseInt(bulkFormData.count, 10),
      seat_count: parseInt(bulkFormData.seat_count, 10),
      type: bulkFormData.type,
    };

    try {
      setSubmitting(true);
      setSubmitError("");
      const response = await fetchWithAuth(`${API}/tables/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Error saving bulk table data");
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error saving tables in bulk:", error);
      setSubmitError(error.message || "Настана грешка при креирање на масите.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (mode === "bulk" && !isEdit) {
      handleBulkSubmit();
      return;
    }
    handleSingleSubmit();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      keepMounted
      className="table-modal"
      PaperProps={{ className: "custom-dialog-paper" }}
    >
      <DialogTitle className="modal-title">
        {isEdit ? "Измени маса" : "Додади маса"}
      </DialogTitle>

      <DialogContent dividers className="modal-content">
        {!isEdit && (
          <Tabs
            value={mode}
            onChange={(_, newMode) => setMode(newMode)}
            className="table-modal-tabs"
            variant="fullWidth"
          >
            <Tab value="single" label="Додади маса" />
            <Tab value="bulk" label="Додади повеќе маси оддеднаш" />
          </Tabs>
        )}

        {submitError ? <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert> : null}

        {(isEdit || mode === "single") && (
          <>
            <Box mb={2}>
              <TextField
                name="name"
                label="Име на маса"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                className="modal-input"
              />
            </Box>

            <Box display="flex" gap={2} mb={2}>
              <TextField
                name="number"
                label="Број на маса"
                value={formData.number}
                onChange={handleNumberInputChange}
                fullWidth
                className="modal-input"
                InputProps={{ inputProps: { min: 1 } }}
                error={errors.number}
                helperText={errors.number ? "Внесете број" : ""}
              />
              <TextField
                name="seat_count"
                label="Број на столчиња"
                value={formData.seat_count}
                onChange={handleNumberInputChange}
                fullWidth
                className="modal-input"
                InputProps={{ inputProps: { min: 1 } }}
                error={errors.seat_count}
                helperText={errors.seat_count ? "Внесете број" : ""}
              />
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" mb={1}>
                Вид на маса:
              </Typography>
              <ToggleButtonGroup
                value={formData.type}
                exclusive
                onChange={handleTypeChange}
                className="table-type-buttons"
              >
                <ToggleButton value="round">
                  <TableBarIcon />
                  <span>Кружна</span>
                </ToggleButton>
                <ToggleButton value="square">
                  <TableRestaurantIcon />
                  <span>Правоаголна</span>
                </ToggleButton>
                <ToggleButton value="standing">
                  <LocalBarIcon />
                  <span>Коктел</span>
                </ToggleButton>
                <ToggleButton value="band">
                  <RadioIcon />
                  <span>Бенд</span>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </>
        )}

        {!isEdit && mode === "bulk" && (
          <>
            <Box display="flex" gap={2} mb={2}>
              <TextField
                name="count"
                label="Број на маси"
                value={bulkFormData.count}
                onChange={handleBulkNumberInputChange}
                fullWidth
                className="modal-input"
                InputProps={{ inputProps: { min: 1 } }}
                error={bulkErrors.count}
                helperText={bulkErrors.count ? "Внесете валиден број" : ""}
              />
              <TextField
                name="seat_count"
                label="Број на столчиња"
                value={bulkFormData.seat_count}
                onChange={handleBulkNumberInputChange}
                fullWidth
                className="modal-input"
                InputProps={{ inputProps: { min: 1 } }}
                error={bulkErrors.seat_count}
                helperText={bulkErrors.seat_count ? "Внесете валиден број" : ""}
              />
            </Box>

            <Box mb={2}>
              <Typography variant="subtitle2" mb={1}>
                Вид на маса:
              </Typography>
              <ToggleButtonGroup
                value={bulkFormData.type}
                exclusive
                onChange={handleBulkTypeChange}
                className="table-type-buttons"
              >
                <ToggleButton value="round">
                  <TableBarIcon />
                  <span>Кружна</span>
                </ToggleButton>
                <ToggleButton value="square">
                  <TableRestaurantIcon />
                  <span>Правоаголна</span>
                </ToggleButton>
                <ToggleButton value="standing">
                  <LocalBarIcon />
                  <span>Коктел</span>
                </ToggleButton>
                <ToggleButton value="band">
                  <RadioIcon />
                  <span>Бенд</span>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions className="table-actions">
        <Button onClick={onClose} variant="outlined" className="table-cancel" disabled={submitting}>
          Откажи
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className="table-submit"
          disabled={submitting}
        >
          {submitting
            ? "Се зачувува..."
            : isEdit
            ? "Зачувај измени"
            : mode === "bulk"
            ? "Креирај маси"
            : "Креирај маса"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TableModal;
